// src/components/inventory/LoadCardAction.js
//
// THE ACTION ROW ON A TRANSFER CARD -- one box per item and the button.
// Built at #630, Session 79.22b.1, inside TransfersPage.js; MOVED here at
// Session 79.22b.4.5 (#665), unchanged in what it already did, when it became
// the row all four job cards share and gained what gate 665 F5 asks of it:
//
//   - `above`: a slot over the boxes, for the To load card's route choice and
//     the line naming the driver and the load number;
//   - a line with NO box (`noBox`), for an item with nothing left to do on
//     this tab, or refused (gate 665 F1, Partial);
//   - A TAPPED BOX SELECTS ITS NUMBER, so typing replaces it (D20). The
//     pre-filled number had to be deleted first, which is Baymard's finding.
//     All four job cards get it, because they share this box;
//   - `note`: the answer to a press, ON THE CARD, where the thumb already is
//     (D21's line), with its Undo beside it when the page passes `onUndo` --
//     #694, built at 79.22b.4.6 for Sign off; Send, Collect and Count in follow
//     at 79.22b.4.6.1 and 4.6.2 -- and a refusal inline under the button with
//     the typed numbers kept (F1, Error).
//
// ── The defect #630 fixed, in plain words ──
//
// A driver opened To collect and saw cards he could not do anything with. No
// button, nothing to type a number into. To get the material onto his truck he
// tapped the card, read a sheet, scrolled past the history and only then found
// the quantity box and the button -- four or five presses, on the tab where he
// does his job. Two tabs away, To make had put its action straight on the card
// since #553, which is the pattern he had already learned.
//
// COLBY'S SHAPE, ruled at 79.22b.1 after he read a mockup that got it wrong:
// EVERY line gets its own box with the item name beside it, and the button sits
// below all of them. The first draft offered a single box only when a load
// carried one item and fell back to a plain button otherwise -- and he caught
// what that misses: "this becomes an issue for a multi line order that is
// missing some of what is supposed to be there."
//
// ── THE CARD TAKES. THE PANEL COUNTS. ──
//
// Lowering a box on To collect means "I am taking fewer and the rest stays at
// the yard" -- always honest, nothing raised. Saying "less was actually here
// than Houston declared" is a different act: it writes a discrepancy with a
// dollar value against a named leg, needs a reason in words, and has no honest
// home when two trucks are standing at one yard. `CollectPanel` already refuses
// to count in that case, so the card does not offer it and its footer sends the
// driver into the transfer for the shortfall.
//
// On To count in the two ARE the same act -- what you count is what is booked
// -- so the box is the count, a lowered box says so on the line, and the
// over-count refusal the panel uses is passed in by the caller rather than
// written again here.
//
// ── What a box holds ──
//
// Untyped means "all of it": the common answer costs no typing. As typed, a
// thing that is not a number, or is below zero, counts as zero (gate 665 X2).
// Above the line's ceiling it is capped -- unless `allowOver`, where the caller's
// `guard` answers it in words instead, which is how To make, To load and To
// count in refuse a number they cannot take.

import React, { useState, useRef } from 'react';
import ActionButton from '../ActionButton';
import { useTier, controlHeight, CONTROL } from '../../lib/tiers';
import { UNDO_LABEL, OPEN_TRANSFER_LABEL } from '../../lib/transferCardJobs';
import { qtyBox, QTY_BOX_CLASS } from '../../lib/qtyBox';

// Selecting on focus, in every browser that will let it. select() alone is
// undone on some phones by the tap's own mouseup, which is why the caller also
// holds that mouseup back once (below).
function selectAll(el) {
  if (!el) return;
  try { el.select(); } catch (_) { /* nothing to select */ }
  try { el.setSelectionRange(0, String(el.value ?? '').length); } catch (_) { /* not a text box */ }
}

// ── #704, gate 665 F4 and D5, Session 79.22b.4.6.1c -- THE CARD'S SECOND DOOR ──
//
// Colby, on a To load card on his desk: "The average user wont expect a card
// click on desktop to open the full sheet." The top of the card opens the
// transfer (D5), and nothing on the card said so -- while its own footer told
// people to "open the transfer". So every card that acts carries a button by
// that name. It OPENS and never writes; D5 stands, and the card's top still
// opens the same sheet.
//
// Not a commit control, so: 40 to a finger, the minimum for anything
// interactive, and 32 to a mouse -- the fine pointer's DEFAULT, which tiers.js
// states in words ("Fine: default 32") and carries as its commit value. Not 24:
// that is the floor, and the approved drawing sets this button level with the
// button beside it on a desk.
export function openTransferHeight(coarse) {
  return coarse ? CONTROL.coarse.min : CONTROL.fine.commit;
}

export function OpenTransferButton({ onOpen }) {
  const { coarse } = useTier();
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        order: 1, flex: 'none', minHeight: openTransferHeight(coarse), padding: '0 16px',
        borderRadius: 8, border: '1px solid #CFCFCF', background: '#FFFFFF', color: '#3A3A3A',
        fontWeight: 600, fontSize: 14, cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
      }}
    >
      {OPEN_TRANSFER_LABEL}
    </button>
  );
}

// ActionButton draws a refused press's reason straight after the button, as a
// sibling. In the desk row that would squeeze it between the two buttons, so a
// caller passes this in `reasonStyle` and the reason takes its own line under
// both -- still visible before the press and still answering it (#492).
export const REASON_OWN_LINE = { order: 2, flexBasis: '100%' };

// The row a card's own button sits in -- the drawing Colby approved (R1 at
// 79.22b.4.6.1b's open, and 79.22b.4.6.1c's). On a desk the button is the width
// of its words with Open transfer beside it. On a phone or tablet the button
// keeps its full width for the thumb, unchanged, and Open transfer comes after
// everything else, under the footer. Width decides the layout (tiers.js D2);
// the caller decides the button's own width from the same `phoneLayout`.
export function CardButtonRow({ phoneLayout, onOpen = null, children, after = null }) {
  if (phoneLayout) {
    return (
      <>
        {children}
        {after}
        {onOpen && <div style={{ marginTop: 8 }}><OpenTransferButton onOpen={onOpen} /></div>}
      </>
    );
  }
  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        {children}
        {onOpen && <OpenTransferButton onOpen={onOpen} />}
      </div>
      {after}
    </>
  );
}

// The answer to a press, drawn the same way on a live card and on a card kept
// in place after its job there is done (TransfersPage's pinned card).
//
// #694 (gate 665 D21, Screen 9) -- WITH ITS UNDO, beside the words, where the
// thumb already is. Offered only when the answer carries `undo` (the database
// named every run the press wrote) and the page gave a handler; it lasts as
// long as the answer does. An Undo WRITES, so it is a commit control: 48px to
// a finger, 32px to a mouse (#417 D6). The hook is called before the early
// return, never after it - rules-of-hooks, which #417's foundation broke once.
export function CardNote({ note, where = 'above', onUndo = null, busy = false }) {
  const { coarse } = useTier();
  if (!note) return null;
  if (note.kind === 'error') {
    return where === 'below' ? (
      <div role="alert" style={{
        marginTop: 8, padding: '8px 10px', borderRadius: 8,
        background: '#FDF1F1', border: '1px solid #F0CFCF',
        fontSize: 13, color: '#8E2F2F', fontWeight: 600, lineHeight: 1.4,
      }}>{note.text}</div>
    ) : null;
  }
  if (where !== 'above') return null;
  const undoable = !!(note.undo && onUndo);
  return (
    <div role="status" style={{
      marginBottom: 8, padding: '8px 10px', borderRadius: 8,
      background: '#F1FAF6', border: '1px solid #CFE9DE',
      fontSize: 13, color: '#14503B', fontWeight: 700, lineHeight: 1.4,
      ...(undoable ? { display: 'flex', alignItems: 'center', gap: 10 } : {}),
    }}>
      {undoable ? <span style={{ flex: 1, minWidth: 0 }}>{note.text}</span> : note.text}
      {undoable && (
        <ActionButton
          onClick={() => onUndo(note)}
          busy={busy}
          busyLabel="Undoing…"
          aria-label={`${UNDO_LABEL}: ${note.text}`}
          style={{
            flex: 'none', minHeight: controlHeight(coarse, 'commit'), padding: '0 16px',
            borderRadius: 8, border: '1px solid #14503B', background: '#FFFFFF',
            color: '#14503B', fontWeight: 700, fontSize: 14, cursor: 'pointer',
          }}
        >
          {UNDO_LABEL}
        </ActionButton>
      )}
    </div>
  );
}

// A card whose job on this tab is finished by the press, kept in its place
// with its answer until the next action (gate 665 F3). No boxes, no button -
// except the answer's own Undo, which is why a finished card stays put (#694),
// and Open transfer under it (#704, the drawing's "after" state).
export function CardNoteOnly({ note, onUndo = null, busy = false, onOpen = null }) {
  if (!note) return null;
  return (
    <div style={{ borderTop: '1px solid #F0F0F0', padding: '10px 12px 12px' }}>
      <CardNote note={note} where="above" onUndo={onUndo} busy={busy} />
      <CardNote note={note} where="below" />
      {note.kind === 'done' && note.after && (
        <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.4 }}>{note.after}</div>
      )}
      {onOpen && <div style={{ marginTop: 8 }}><OpenTransferButton onOpen={onOpen} /></div>}
    </div>
  );
}

export default function LoadCardAction({
  lines, busy, accent, label, footer,
  lineNote = null, guard = null, allowOver = false, onSubmit,
  above = null, note = null, onUndo = null, onOpen = null,
}) {
  const [typed, setTyped] = useState({});
  const { coarse, phoneLayout } = useTier();
  // The box a tap has just focused, so the tap's own mouseup cannot undo the
  // selection it made (D20).
  const justFocused = useRef(null);

  const boxed = lines.filter(l => !l.noBox);

  // Untyped means "all of it", which is the common answer and costs no taps.
  // The same default the collect panel's own boxes use.
  const valueOn = (l) => {
    const t = typed[String(l.item_id)];
    if (t === '' || t == null) return l.max;
    const n = Number(t);
    if (!Number.isFinite(n) || n < 0) return 0;
    return allowOver ? n : Math.min(n, l.max);
  };

  const values = {};
  boxed.forEach(l => { values[String(l.item_id)] = valueOn(l); });
  const total = boxed.reduce((n, l) => n + valueOn(l), 0);
  const blockedReason = guard ? guard(values, total) : null;
  const done = note && note.kind === 'done' ? note : null;

  return (
    <div style={{ borderTop: '1px solid #F0F0F0' }}>
      {above && <div style={{ padding: '10px 12px 0' }}>{above}</div>}
      <div style={{ padding: '2px 12px' }}>
        {lines.map((l, i) => {
          const k = String(l.item_id);
          const lineMsg = !l.noBox && lineNote ? lineNote(l, valueOn(l)) : null;
          return (
            <div key={k} style={{
              display: 'flex', gap: 12, alignItems: 'flex-start', padding: '9px 0',
              borderBottom: i < lines.length - 1 ? '1px solid #F4F4F4' : 'none',
            }}>
              {l.noBox ? (
                // Nothing to type for this item on this tab: the column stays,
                // so every name lines up, and nothing pretends to be a control.
                <div aria-hidden="true" style={{ width: 76, flex: 'none' }} />
              ) : (
                <input
                  type="text" inputMode="decimal"
                  value={typed[k] ?? String(l.max)}
                  onChange={e => setTyped(v => ({ ...v, [k]: e.target.value }))}
                  onFocus={e => { justFocused.current = k; selectAll(e.target); }}
                  onMouseUp={e => {
                    if (justFocused.current === k) { e.preventDefault(); justFocused.current = null; }
                  }}
                  aria-label={`How many ${l.name}`}
                  // #708 -- the count this press confirms, drawn so the eye
                  // lands on it: the shared confirm-box style (src/lib/qtyBox.js).
                  className={QTY_BOX_CLASS}
                  style={{ ...qtyBox({ width: 76, align: 'center' }), flex: 'none' }}
                />
              )}
              <div style={{ minWidth: 0, paddingTop: 2 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#0F0F0F' }}>{l.name}</div>
                <div style={{
                  fontSize: 12, marginTop: 2,
                  // #800 -- an item promised after the needed date reads amber (C).
                  color: l.refused ? '#8A2B22' : l.subLate ? '#8A3A0F' : '#5C5C5C',
                  fontWeight: l.refused ? 700 : l.subLate ? 600 : 400,
                }}>{l.sub}</div>
                {lineMsg && (
                  <div style={{ fontSize: 12, color: '#8A5A10', marginTop: 3, fontWeight: 600 }}>{lineMsg}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ padding: '10px 12px 12px' }}>
        <CardNote note={note} where="above" onUndo={onUndo} busy={busy} />
        <CardButtonRow
          phoneLayout={phoneLayout}
          onOpen={onOpen}
          after={(
            <>
              <CardNote note={note} where="below" />
              {(done?.after || footer) && (
                <div style={{ fontSize: 12, color: '#5C5C5C', marginTop: 8, lineHeight: 1.4 }}>
                  {done?.after || footer(total, values)}
                </div>
              )}
            </>
          )}
        >
          <ActionButton
            onClick={() => onSubmit(values, total)}
            blockedReason={blockedReason}
            busy={busy} busyLabel="Recording…"
            reasonStyle={{ color: '#48545F', ...(phoneLayout ? {} : REASON_OWN_LINE) }}
            style={{
              // #704 -- full width is for the thumb; on a desk, the width of its words.
              ...(phoneLayout ? { width: '100%' } : { flex: 'none', padding: '0 16px' }),
              minHeight: controlHeight(coarse, 'commit'), borderRadius: 8, border: 0,
              background: accent, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
            }}
          >
            {label(total, values)}
          </ActionButton>
        </CardButtonRow>
      </div>
    </div>
  );
}
